import { RefCallback, useState } from "react";
import useResizeObserver from "use-resize-observer";
import { useDebouncedCallback } from "use-debounce";
import { isPresent } from "./utils/checks";

type ObservedSize = {
  width: number | undefined;
  height: number | undefined;
};

const deltaExceeded =
  (delta: number) =>
  (prevDimension = 0, dimension = 0) => {
    return Math.abs(dimension - prevDimension) > delta;
  };

const deltaSizeExceeded = (
  prevSize: ObservedSize,
  size: ObservedSize,
  delta: number
): boolean => {
  const checkDeltaFn = deltaExceeded(delta);
  return (
    checkDeltaFn(prevSize.width, size.width) ||
    checkDeltaFn(prevSize.height, size.height)
  );
};

export const useDebouncedResizeObserver = (
  wait: number,
  delta?: number
): ObservedSize & {
  ref: RefCallback<HTMLDivElement>;
} => {
  const [size, setSize] = useState<ObservedSize>({
    width: undefined,
    height: undefined
  });

  const onResize = useDebouncedCallback(
    (value: ObservedSize) => {
      const isFirstRender = !isPresent(size.width) || !isPresent(size.height);

      if (isFirstRender || deltaSizeExceeded(size, value, delta || 200)) {
        setSize(value);
      }
    },
    wait,
    { leading: true }
  );
  const { ref } = useResizeObserver<HTMLDivElement>({ onResize });

  return { ref, ...size };
};
